// Accesos de emergencia al expediente (MT-042, break-glass) — pantalla de revisión del Responsable de privacidad (el administrador del consultorio). Mismo guard de
// sesión que ComitePage.tsx (leerSesionHceAdmin, NUNCA la de Medplum). El motivo sale descifrado solo para esta sesión de administrador.
// Las PENDIENTES se piden aparte y completas (`?pendientes=1`): ninguna se pierde por antigua. Las revisadas se pagan (50 por página, «Ver más»).
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { leerSesionHceAdmin } from '@/auth/hceAdminSession';
import { listarPendientes, listarRevisadas, marcarEmergenciaRevisada, vigenciaDe, type EmergenciaAcceso } from '@/lib/hceEmergencias';

const NOTA_MIN = 3;
const NOTA_MAX = 500;
const formato = (ms: number): string => new Date(ms).toLocaleString('es-EC', { dateStyle: 'short', timeStyle: 'short' });

function PanelRevision({ emergencia, token, onRevisada }: { emergencia: EmergenciaAcceso; token: string; onRevisada: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valida = nota.trim().length >= NOTA_MIN;

  async function marcar(): Promise<void> {
    setEnviando(true);
    setError(null);
    try {
      await marcarEmergenciaRevisada(token, emergencia.id, nota.trim());
      onRevisada();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="mt-3 space-y-2 rounded-md border border-border bg-hce-paper p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('emergencias.nota.titulo')}</p>
      <Textarea aria-label={t('emergencias.nota.titulo')} placeholder={t('emergencias.nota.placeholder')} value={nota} onChange={(e) => setNota(e.target.value)} rows={2} maxLength={NOTA_MAX} />
      <p className="text-xs text-hce-muted">{t('emergencias.nota.ayuda', { min: NOTA_MIN, actual: nota.trim().length, max: NOTA_MAX })}</p>
      {error && (
        <Alert variant="red" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button size="sm" disabled={!valida || enviando} onClick={() => void marcar()}>
        {enviando ? t('emergencias.marcando') : t('emergencias.marcarRevisado')}
      </Button>
    </div>
  );
}

export function EmergenciasPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sesion = leerSesionHceAdmin();
  const [pendientes, setPendientes] = useState<EmergenciaAcceso[] | null>(null);
  const [revisadas, setRevisadas] = useState<EmergenciaAcceso[]>([]);
  const [hayMasRevisadas, setHayMasRevisadas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abierta, setAbierta] = useState<number | null>(null);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true);
    setError(null);
    try {
      const [p, r] = await Promise.all([listarPendientes(sesion.token), listarRevisadas(sesion.token)]);
      setPendientes(p);
      setRevisadas(r.filas);
      setHayMasRevisadas(r.hayMas);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('emergencias.errorCargar'));
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function verMasRevisadas(): Promise<void> {
    if (!sesion || revisadas.length === 0) return;
    setCargandoMas(true);
    try {
      const r = await listarRevisadas(sesion.token, revisadas[revisadas.length - 1]!.id);
      setRevisadas((previas) => [...previas, ...r.filas]);
      setHayMasRevisadas(r.hayMas);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('emergencias.errorCargar'));
    } finally {
      setCargandoMas(false);
    }
  }

  if (!sesion) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-hce-paper px-4">
        <Alert variant="red" className="max-w-sm">
          <AlertDescription>{t('admin.usuarios.sesionVencida')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('emergencias.titulo')}</h1>
          <Button variant="outline" size="sm" onClick={() => void navigate('/usuarios')}>
            {t('emergencias.volver')}
          </Button>
        </div>
        <p className="text-sm text-hce-muted">{t('emergencias.descripcion')}</p>

        {error && (
          <Alert variant="red" role="alert">
            <AlertDescription className="flex items-center gap-3">
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={() => void cargar()}>
                {t('emergencias.reintentar')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {cargando && !pendientes ? (
          <p className="text-sm text-hce-muted" role="status">
            {t('emergencias.cargando')}
          </p>
        ) : !error && (pendientes ?? []).length === 0 ? (
          <p className="text-sm text-hce-muted" data-testid="sin-pendientes">
            {t('emergencias.sinPendientes')}
          </p>
        ) : (
          <div className="space-y-2">
            {(pendientes ?? []).map((f) => (
              <div key={f.id} className="rounded-lg border border-border bg-hce-surface p-3" data-testid="emergencia-pendiente">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-0.5 text-sm text-hce-ink">
                    <div className="font-medium">{f.profesional ?? f.practitionerId}</div>
                    <div className="font-mono text-xs text-hce-muted">
                      {t('emergencias.paciente')} Patient/{f.pacienteFhirId} · {t('emergencias.abierta')} {formato(f.desde)}
                    </div>
                    <div className="text-xs text-hce-muted">
                      {vigenciaDe(f.hasta) === 'vigente' ? t('emergencias.venceEl', { cuando: formato(f.hasta) }) : t('emergencias.vencioEl', { cuando: formato(f.hasta) })}
                    </div>
                    <div className="pt-1">
                      <span className="text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('emergencias.motivo')}: </span>
                      {f.motivo}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <Badge variant={vigenciaDe(f.hasta) === 'vigente' ? 'destructive' : 'clay'}>{vigenciaDe(f.hasta) === 'vigente' ? t('emergencias.vigente') : t('emergencias.vencido')}</Badge>
                    <Button variant="outline" size="sm" aria-expanded={abierta === f.id} aria-controls={`revision-${f.id}`} onClick={() => setAbierta(abierta === f.id ? null : f.id)}>
                      {t('emergencias.revisar')}
                    </Button>
                  </div>
                </div>
                {abierta === f.id && (
                  <div id={`revision-${f.id}`}>
                    <PanelRevision
                      emergencia={f}
                      token={sesion.token}
                      onRevisada={() => {
                        setAbierta(null);
                        void cargar();
                      }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {revisadas.length > 0 && (
          <div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-hce-muted">{t('emergencias.historial')}</p>
            <div className="mt-2 space-y-1.5">
              {revisadas.map((f) => (
                <div key={f.id} className="rounded-md border border-border bg-hce-surface px-3 py-2 text-sm" data-testid="emergencia-revisada">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-hce-ink">{f.profesional ?? f.practitionerId}</span>
                    <Badge variant="sage">{t('emergencias.revisada')}</Badge>
                  </div>
                  <div className="font-mono text-xs text-hce-muted">
                    Patient/{f.pacienteFhirId} · {formato(f.desde)}
                  </div>
                  <div className="text-xs text-hce-muted">
                    {t('emergencias.motivo')}: {f.motivo}
                  </div>
                  <div className="text-xs text-hce-muted">
                    {t('emergencias.revisadaPor', { quien: f.revisadoPor ?? '—', cuando: formato(f.revisadoAt!) })}: {f.notaRevision}
                  </div>
                </div>
              ))}
            </div>
            {hayMasRevisadas && (
              <Button variant="outline" size="sm" className="mt-2" disabled={cargandoMas} onClick={() => void verMasRevisadas()}>
                {cargandoMas ? t('emergencias.cargando') : t('emergencias.verMas')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
